import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  Pressable,
  ScrollView,
  Platform,
  Dimensions,
  Alert,
  StyleSheet,
} from 'react-native';
import { usePathname } from 'expo-router';
import { useTheme } from '../../contexts/ThemeContext';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { Input, Button, IconButton } from '../ui';
import { addColorOpacity } from '../../shared/utils/styles';
import {
  FeedbackType,
  FeedbackPriority,
  CreateFeedbackRequest,
  DeviceInfo,
} from '../../shared/types/feedback';
import { Ionicons } from '@expo/vector-icons';

export interface FeedbackModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (feedback: CreateFeedbackRequest) => Promise<void>;
  initialType?: FeedbackType;
}

const FEEDBACK_OPTIONS: { 
  value: FeedbackType; 
  label: string; 
  emoji: string;
  preSelectedTitle: string;
}[] = [
  { 
    value: 'bug', 
    label: 'Something isn\'t working', 
    emoji: '🐛',
    preSelectedTitle: 'Something isn\'t working'
  },
  { 
    value: 'feature_request', 
    label: 'I want a new feature', 
    emoji: '💡',
    preSelectedTitle: 'I would like a new feature'
  },
  { 
    value: 'ui_ux', 
    label: 'This is hard to use', 
    emoji: '🎨',
    preSelectedTitle: 'This is hard to use or confusing'
  },
  { 
    value: 'general', 
    label: 'I have a question or comment', 
    emoji: '💬',
    preSelectedTitle: 'I have a question or comment'
  },
];

export function FeedbackModal({
  visible,
  onClose,
  onSubmit,
  initialType,
}: FeedbackModalProps) {
  const styles = useStyles();
  const responsive = useResponsive();
  const pathname = usePathname();

  const [type, setType] = useState<FeedbackType>(initialType || 'general');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      setDescription('');
      setType(initialType || 'general');
    }
  }, [visible, initialType]);

  const handleSubmit = async () => {
    if (!description.trim()) {
      Alert.alert('Required', 'Please tell us what you need help with.');
      return;
    }

    setLoading(true);
    try {
      const dimensions = Dimensions.get('window');
      const selectedOption = FEEDBACK_OPTIONS.find(opt => opt.value === type);
      const title = selectedOption?.preSelectedTitle || 'Feedback';

      const deviceInfo: DeviceInfo = {
        platform: Platform.OS === 'web' ? 'web' : Platform.OS,
        userAgent: Platform.OS === 'web' && typeof navigator !== 'undefined' 
          ? navigator.userAgent 
          : undefined,
        screenWidth: dimensions.width,
        screenHeight: dimensions.height,
        deviceType: responsive.isMobile ? 'mobile' 
          : responsive.isTablet ? 'tablet' 
          : 'desktop',
        orientation: dimensions.width > dimensions.height ? 'landscape' : 'portrait',
      };

      const feedback: CreateFeedbackRequest = {
        type,
        priority: 'medium',
        title,
        description: description.trim(),
        currentPage: pathname || undefined,
        deviceInfo,
        metadata: {
          route: pathname,
          responsiveBreakpoint: responsive.isMobile ? 'mobile' 
            : responsive.isTablet ? 'tablet' 
            : 'desktop',
          screenSize: `${dimensions.width}x${dimensions.height}`,
        },
      };

      await onSubmit(feedback);
      setDescription('');
      setType(initialType || 'general');
      Alert.alert('Thank You!', 'Your feedback has been sent. We\'ll get back to you soon.');
      onClose();
    } catch (error: any) {
      const errorMessage = error?.message || error?.data?.message || 'Failed to send feedback. Please try again.';
      Alert.alert('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const modalStyles = makeModalStyles(styles, responsive);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.modalOverlay} onPress={onClose}>
        <Pressable
          style={modalStyles.modalContent}
          onPress={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <View style={modalStyles.header}>
            <View style={modalStyles.headerContent}>
              <Text style={modalStyles.title}>Send Feedback</Text>
              <Text style={modalStyles.subtitle}>What would you like to tell us?</Text>
            </View>
            <IconButton
              icon="close"
              onPress={onClose}
              variant="ghost"
              size="md"
            />
          </View>

          {/* Content */}
          <ScrollView
            style={modalStyles.scrollView}
            contentContainerStyle={modalStyles.scrollContent}
            showsVerticalScrollIndicator={true}
          >
            {/* Type Selection */}
            <View style={modalStyles.section}>
              <Text style={modalStyles.sectionLabel}>What is this about?</Text>
              <View style={modalStyles.optionsContainer}>
                {FEEDBACK_OPTIONS.map((option) => (
                  <Pressable
                    key={option.value}
                    onPress={() => setType(option.value)}
                    style={({ pressed }) => [
                      modalStyles.optionButton,
                      type === option.value && modalStyles.optionButtonActive,
                      pressed && modalStyles.optionButtonPressed,
                    ]}
                  >
                    <Text style={modalStyles.optionEmoji}>{option.emoji}</Text>
                    <Text style={[
                      modalStyles.optionText,
                      type === option.value && modalStyles.optionTextActive,
                    ]}>
                      {option.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>

            {/* Description Input */}
            <View style={modalStyles.section}>
              <Text style={modalStyles.sectionLabel}>Tell us more</Text>
              <Input
                placeholder="Describe your feedback in detail..."
                value={description}
                onChangeText={setDescription}
                multiline
                numberOfLines={responsive.isMobile ? 5 : 7}
                style={modalStyles.textInput}
                placeholderTextColor={addColorOpacity(styles.tokens.color.onSurface, 0.5)}
              />
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={modalStyles.footer}>
            <Button
              title="Cancel"
              onPress={onClose}
              variant="outline"
              style={modalStyles.footerButton}
              size={responsive.isSmallMobile ? 'sm' : undefined}
            />
            <Button
              title={loading ? 'Sending...' : (responsive.isSmallMobile ? 'Send' : 'Send Feedback')}
              onPress={handleSubmit}
              disabled={loading || !description.trim()}
              style={modalStyles.footerButton}
              size={responsive.isSmallMobile ? 'sm' : undefined}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function makeModalStyles(
  styles: ReturnType<typeof useStyles>,
  responsive: ReturnType<typeof useResponsive>
) {
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile, isTablet } = responsive;

  return StyleSheet.create({
    modalContent: {
      backgroundColor: tokens.color.background,
      borderRadius: utils.radius.lg,
      width: isSmallMobile ? '95%' : isMobile ? '92%' : isTablet ? '85%' : 600,
      maxWidth: 600,
      maxHeight: isSmallMobile ? '95%' : '90%',
      borderWidth: 1,
      borderColor: tokens.color.border,
      overflow: 'hidden',
      ...Platform.select({
        web: {
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }),
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.12,
      shadowRadius: 32,
      elevation: 8,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: isSmallMobile ? utils.spacing.md : utils.spacing.lg,
      paddingBottom: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    headerContent: {
      flex: 1,
      marginRight: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      minWidth: 0,
    },
    title: {
      fontSize: isSmallMobile ? utils.typography.sizes.lg : utils.typography.sizes['2xl'],
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      marginBottom: isSmallMobile ? 2 : utils.spacing.xs,
      fontFamily: utils.typography.heading.fontFamily,
    },
    subtitle: {
      fontSize: isSmallMobile ? utils.typography.sizes.xs : utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onBackground, 0.7),
      fontFamily: utils.typography.body.fontFamily,
    },
    scrollView: {
      flex: 1,
    },
    scrollContent: {
      padding: isSmallMobile ? utils.spacing.md : utils.spacing.lg,
      paddingBottom: utils.spacing.md,
    },
    section: {
      marginBottom: isSmallMobile ? utils.spacing.lg : utils.spacing.xl,
    },
    sectionLabel: {
      fontSize: isSmallMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      marginBottom: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      fontFamily: utils.typography.body.fontFamily,
    },
    optionsContainer: {
      gap: isSmallMobile ? utils.spacing.xs : utils.spacing.sm,
    },
    optionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      paddingVertical: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      borderRadius: utils.radius.md,
      borderWidth: 1,
      borderColor: tokens.color.border,
      backgroundColor: tokens.color.surface,
      minHeight: isSmallMobile ? 48 : 56,
      ...Platform.select({
        web: {
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        },
      }),
    },
    optionButtonActive: {
      borderColor: tokens.color.primary,
      borderWidth: 2,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
      ...Platform.select({
        web: {
          boxShadow: `0 2px 8px ${addColorOpacity(tokens.color.primary, 0.2)}`,
        },
      }),
    },
    optionButtonPressed: {
      opacity: 0.8,
    },
    optionEmoji: {
      fontSize: isSmallMobile ? 20 : 24,
      marginRight: isSmallMobile ? utils.spacing.xs : utils.spacing.sm,
      flexShrink: 0,
    },
    optionText: {
      fontSize: isSmallMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      flex: 1,
      flexWrap: 'wrap',
    },
    optionTextActive: {
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.semibold,
    },
    textInput: {
      minHeight: isSmallMobile ? 100 : isMobile ? 120 : 140,
      textAlignVertical: 'top',
    },
    footer: {
      flexDirection: 'row',
      gap: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      padding: isSmallMobile ? utils.spacing.md : utils.spacing.lg,
      paddingTop: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
      borderTopWidth: 1,
      borderTopColor: tokens.color.border,
      backgroundColor: tokens.color.surface,
    },
    footerButton: {
      flex: 1,
    },
  });
}
